import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { toast } from '@/hooks/use-toast'
import { SetupPageShell } from '@/features/admin/components/SetupPageShell'
import { ConnectionState, formatTimestamp } from '@/features/admin/components/ConnectionState'
import {
  useUpdateWorkflowEmailSettings,
  useWorkflowEmailSettings,
} from '@/features/admin/workflowSetupQueries'
import type { ApiRequestError } from '@/services/api'

const schema = z.object({
  address: z.string(),
  graph_tenant_id: z.string(),
  graph_client_id: z.string(),
  client_secret: z.string(),
  use_default_inbox: z.boolean(),
  inbox: z.string(),
  in_progress: z.string(),
  handled: z.string(),
  not_handled: z.string(),
})

type FormValues = z.infer<typeof schema>

const empty: FormValues = {
  address: '',
  graph_tenant_id: '',
  graph_client_id: '',
  client_secret: '',
  use_default_inbox: false,
  inbox: '',
  in_progress: '',
  handled: '',
  not_handled: '',
}

export function EmailSettingsPage() {
  const { organizationId, workflowId } = useParams<{ organizationId: string; workflowId: string }>()
  const navigate = useNavigate()
  const { data: settings } = useWorkflowEmailSettings(organizationId, workflowId)
  const update = useUpdateWorkflowEmailSettings(organizationId!, workflowId!)

  const { register, handleSubmit, reset, watch, setValue } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: empty,
  })

  useEffect(() => {
    if (settings) reset({ ...empty, ...settings })
  }, [settings, reset])

  const usesDefaultInbox = watch('use_default_inbox')
  const clientSecretValue = watch('client_secret')

  const onSubmit = handleSubmit(async (values) => {
    try {
      await update.mutateAsync(values)
      toast({ title: 'Mailbox settings saved' })
      navigate(`/admin/organizations/${organizationId}/workflows/${workflowId}/setup`)
    } catch (error) {
      toast({
        title: 'Could not save',
        description: (error as ApiRequestError)?.message,
        variant: 'destructive',
      })
    }
  })

  return (
    <SetupPageShell
      organizationId={organizationId}
      workflowId={workflowId}
      title="Mailbox connection"
      description="Which account the workflow reads, and the folders it uses as its queue."
    >
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-md bg-gray-50 px-4 py-3">
        <ConnectionState state={settings?.connection_state ?? 'unconfigured'} />
        <div className="text-xs text-gray-500">
          Last connected{' '}
          <span className="font-medium text-gray-700">
            {formatTimestamp(settings?.last_connected_at ?? null)}
          </span>
          {'. '}
          Last tried{' '}
          <span className="font-medium text-gray-700">
            {formatTimestamp(settings?.last_connection_attempt_at ?? null)}
          </span>
          {'.'}
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Address</label>
          <Input {...register('address')} placeholder="orders@example.com" />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Graph tenant ID</label>
            <Input {...register('graph_tenant_id')} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Graph client ID</label>
            <Input {...register('graph_client_id')} />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Client secret</label>
          <PasswordInput
            autoComplete="new-password"
            {...register('client_secret')}
            placeholder="******************"
            hasValue={!!clientSecretValue}
          />
          <p className="text-xs text-gray-400">
            Leave this blank to keep the current secret.
          </p>
        </div>

        <hr className="border-gray-100" />

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={usesDefaultInbox}
            onChange={(e) => setValue('use_default_inbox', e.target.checked)}
          />
          Read the account's default inbox
        </label>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Inbox folder</label>
          <Input
            {...register('inbox')}
            disabled={usesDefaultInbox}
            placeholder="Ordersystem/Inbox"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">In progress</label>
            <Input {...register('in_progress')} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Handled</label>
            <Input {...register('handled')} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Not handled</label>
            <Input {...register('not_handled')} />
          </div>
        </div>
        <p className="text-xs text-gray-400">
          The folders will be created if they don't exist. Please ensure that they do not conflict with existing folders.
        </p>

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-gray-400">
            Saving clears the connection state until a run reaches the mailbox again.
          </p>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </SetupPageShell>
  )
}
